/* eslint-disable @typescript-eslint/no-shadow */
import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { makeStyles } from "@material-ui/core/styles";
import {
  KButton,
  KDialog,
  KIcon,
  KAvatarMulti,
} from "@kyburz/kdesign-system";

import {
  getVehicleGroups,
  deleteVehicleGroup,
} from "../../apiCalls/admin/vehicleGroups";
import { setAlert } from "../../redux/actions/actions";
import { formatDate } from "../../utils/timeUtils";
import useSetState from "../../hooks/useSetState";

import NewVehicleGroup from "./NewVehicleGroup";
import { RootStateInterface } from "../../interfaces/ReduxInterface";
import KInfiniteTable from "../../components/table/KInfiniteTable";

const MemoizedKInfiniteTable = React.memo(
  (props: any) => (
    <KInfiniteTable
      data={props.data}
      hasNextPage={props.hasNextPage}
      isNextPageLoading={props.isNextPageLoading}
      loadNextPage={props.loadNextPage}
      columns={props.columns}
      manualSortBy={true}
      sortTable={props.sortTable}
      initialSortBy={props.initialSortBy}
      hasStickyColumns={true}
      hasFilter
      manualFilters
      filterTable={props.filterTable}
      loading={props.loading}
    />
  ),
  (oldProps, newProps) => {
    return (
      oldProps.data === newProps.data && oldProps.loading === newProps.loading
    );
  },
);

const useStyles = makeStyles(() => ({
  ctaContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
  iconContainer: {
    display: "inline-block",
    cursor: "pointer",
  },
  tableContainer: {
    height: "calc(100% - 100px)",
  },
}));

const VehicleGroups = () => {
  const currentCustomer = useSelector(
    (state: RootStateInterface) => state.customer.currentCustomer,
  );
  const classes = useStyles();
  const firstUpdate = React.useRef(true);
  const dispatch = useDispatch();
  const history = useHistory();

  const { CancelToken } = axios;

  const defaultSortingRules = {
    id: "name",
    order: "asc",
  };

  const [
    {
      query,
      pagesLoaded,
      hasNextPage,
      isNextPageLoading,
      items,
      newGroupOpen,
      reload,
      groupToDelete,
      deleteModalOpen,
      cancelTokenSource,
      orderBy,
      order,
      filterBy,
      loading,
    },
    setState,
  ] = useSetState({
    query: "",
    pagesLoaded: 0,
    hasNextPage: true,
    isNextPageLoading: false,
    items: [],
    newGroupOpen: false,
    groupToDelete: "",
    reload: false,
    deleteModalOpen: false,
    cancelTokenSource: null,
    orderBy: defaultSortingRules.id,
    order: defaultSortingRules.order,
    filterBy: "",
    loading: true,
  });

  const handleRedirect = () => {
    history.push(`/admin/fleet/groups/create/vehicles`);
  };

  const toggleDeletePopup = (e, row) =>
    setState({ deleteModalOpen: !deleteModalOpen, groupToDelete: row.id });

  const toggleNewGroupPopUp = (withReload = false) =>
    setState({ newGroupOpen: !newGroupOpen, reload: withReload });

  const handleEditClick = (row) =>
    history.push(`/admin/fleet/groups/${row.id}/vehicles`);

  const handleDelete = async () => {
    const res = await deleteVehicleGroup(
      currentCustomer?.shortname,
      groupToDelete,
    );
    setState({ deleteModalOpen: false, groupToDelete: "", query: "" });
    if (res.data) {
      dispatch(setAlert(`Vehicle Group ${groupToDelete} deleted`, "success"));
      setState({ reload: true });
    } else {
      dispatch(setAlert(res.detail, "error"));
    }
  };

  const loadGroupsPage = (query, page, startEmpty = true, filterBy = "") => {
    if (cancelTokenSource !== null) {
      cancelTokenSource.cancel("Search cancelled");
    }
    const source = CancelToken.source();

    setState({
      isNextPageLoading: true,
      cancelTokenSource: source,
      loading: true,
    });
    getVehicleGroups(
      currentCustomer?.shortname,
      source.token,
      page,
      orderBy,
      order,
      query,
      false,
      false,
      30,
      filterBy,
    )
      .then((res) => {
        const previousArray = startEmpty ? [] : items;
        const {
          data: { has_next_page },
        } = res;
        setState({
          items: [...previousArray].concat(
            ...res.data?.data.map((x) => ({
              id: x._id,
              name: x.name,
              users: x.users,
              vehicles: x.vehicles,
              created: x.created,
              modified: x.modified,
              numberOfVehicles: x.vehicles.length,
            })),
          ),
          isNextPageLoading: false,
          pagesLoaded: page,
          hasNextPage: has_next_page,
          cancelToken: null,
          loading: false,
        });
      })
      .catch((e) => {
        setState({
          isNextPageLoading: false,
          pagesLoaded: page,
          cancelToken: null,
        });
        console.error(e);
      });
  };

  const loadNextPage = () =>
    loadGroupsPage(query, pagesLoaded + 1, false, filterBy);

  useEffect(() => {
    if (reload) {
      setState({ reload: false });
      loadGroupsPage(query, 1, true, filterBy);
    }
  }, [reload]);

  useEffect(() => {
    loadGroupsPage(query, 1, true, filterBy);
  }, [order, orderBy, filterBy]);

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    loadGroupsPage(query, 1, true, filterBy);
  }, [query]);

  const sortTable = (data) => {
    const rules = data[0];
    if (rules) {
      setState({
        orderBy: rules.id,
        order: rules.desc ? "desc" : "asc",
      });
    }
  };

  const filterTable = (values: { id: string; value: string }[]): void => {
    const parsedFilter: string = values
      .map((x) => `${x.id}:regex:${x.value}`)
      .join(";");
    setState({ filterBy: parsedFilter });
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
        defaultCanSort: true,
      },
      {
        Header: "Users",
        width: 250,
        disableFilters: true,
        accessor: (row) => row.users,
        Cell: (row) => {
          const elements = row.cell?.row?.original?.users || [];
          const users = elements.map((u) => u.user);
          return (
            <div style={{ marginTop: -4 }}>
              <KAvatarMulti elements={users} max={4} size={28} />
            </div>
          );
        },
      },

      {
        Header: "Number of Vehicles",
        accessor: "numberOfVehicles",
        defaultCanSort: true,
        disableFilters: true,
      },
      {
        disableFilters: true,
        id: "created",
        Header: "Created",
        accessor: (row) => formatDate(row.created),
        defaultCanSort: true,
      },
      {
        disableFilters: true,
        accessor: "actions",
        Cell: (row) => (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginRight: 16,
            }}
          >
            <div
              className={classes.iconContainer}
              onClick={() => handleEditClick(row?.row?.original)}
              style={{ marginRight: "16px" }}
            >
              <KIcon icon="edit" color="#383F51" size={20} />
            </div>
            <div
              className={classes.iconContainer}
              onClick={() =>
                setState({
                  deleteModalOpen: true,
                  groupToDelete: row.row.original.id,
                })
              }
            >
              <KIcon icon="trash-2" color="#383F51" size={20} />
            </div>
          </div>
        ),
        width: 100,
        sticky: "right",
      },
    ],
    [],
  );

  const deletePopup = (
    <KDialog
      size="xs"
      title="Are you sure?"
      open={deleteModalOpen}
      primaryCTAonClick={handleDelete}
      primaryCTAColor="danger"
      primaryCTAText="Delete"
      secondaryCTAonClick={(e) => toggleDeletePopup(e, "")}
      secondaryCTAText="Cancel"
    >
      <p>
        Are you sure you want to delete the vehicle group <b>{groupToDelete}</b>
        ?
      </p>
    </KDialog>
  );

  return (
    <>
      <div className={classes.ctaContainer}>
        <div style={{ flexGrow: 1 }} />
        <KButton
          children="Add Group"
          icon={<KIcon icon="plus" />}
          onClick={handleRedirect}
        />
      </div>
      <div className={classes.tableContainer}>
        <MemoizedKInfiniteTable
          isNextPageLoading={isNextPageLoading}
          hasNextPage={hasNextPage}
          columns={columns}
          data={items}
          loadNextPage={loadNextPage}
          sortTable={sortTable}
          initialSortBy={[defaultSortingRules]}
          filterTable={filterTable}
          loading={loading}
        />
      </div>
      <NewVehicleGroup open={newGroupOpen} handleClose={toggleNewGroupPopUp} />
      {deletePopup}
    </>
  );
};

export default VehicleGroups;
